/**
 * "Share link": build the snapshot on screen and get back a URL to send someone.
 *
 * A deliberately smaller thing than the export dialog next to it — no scope and
 * no matrix. One snapshot, one build, and two links come back:
 *
 *   demo   the site's viewer — device frame, rotate, QR code
 *   raw    the playable itself, for an ad network's preview tool or an iframe
 *
 * Both exist because which one is wanted depends on where it is being pasted,
 * and guessing wrong is a round-trip through someone else's tool. They are shown
 * ONE AT A TIME behind a segmented switch rather than stacked: two long URLs one
 * under the other read as a wall, and the thing being copied should be the thing
 * being looked at.
 *
 * The link is a demo.playin.com.tr one, the same site the version editors'
 * external preview uses — but not the same flow: no mongo record is written and
 * no plan test is spent. See controller/snapshotEditor/previewSnapshot.js.
 *
 * Nothing is opened automatically. A build takes minutes, and a `window.open`
 * that far from the click is exactly what pop-up blockers exist to stop — the
 * version editor's preview loses tabs to this. Here the URL sits in the dialog
 * behind an ordinary link the browser will honour, and a copy button.
 */

import React, { useMemo, useState } from "react";
import _ from "lodash";
import BuildWaitLine from "../buildWaitLine";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { describeJobFailure, previewSnapshot, stageState } from "../../modules/snapshotEditorApi";
import { loadingScreenViews, networkCaption, networkViews } from "../../modules/playEngineExportTargets";
import { formatBytes } from "../../modules/playableSizeEstimate";
import {
  BAR_BG,
  CONTROL_BG,
  CONTROL_BORDER,
  FIELD_LABEL,
  PANEL_BG,
} from "./panelPalette";

const BORDER = "1px solid rgba(255,255,255,0.09)";
const ACCENT = "#4eabb1";

/**
 * What the build can target.
 *
 * "Default" leads and is the default: it is the plain web build, the one that
 * reliably comes out as a single self-contained html and therefore the one a
 * link can always point at. The networks below it are offered for feeding a
 * network's own preview tool — several of them emit a script or an asset folder
 * beside the html, which the server refuses for exactly that reason.
 *
 * Not added to `networkViews` itself: that list is the export dialog's network
 * picker, where "default" is not one of the things being shipped.
 */
const NETWORK_OPTIONS = [
  { name: "default", caption: "Default", logo: null },
  ...networkViews,
];

/** The loading screen the engine uses when nobody asks for one. */
const DEFAULT_LOADING_SCREEN = "blankLoading";

/** Display name for a loading screen id, falling back to the id itself. */
function loadingScreenCaption(id) {
  const view = loadingScreenViews.find((screen) => screen.id === id);
  return view ? view.caption : id;
}

/**
 * The two links, and what each is for.
 *
 * The caption is the whole reason the switch is labelled rather than iconised:
 * "demo" and "raw" mean nothing until you are told which one your ad network's
 * upload form wants.
 */
const LINK_VIEWS = [
  { id: "demo", label: "Demo", field: "url", caption: "Opens the viewer — device frame, rotate, QR code." },
  { id: "raw", label: "Raw", field: "rawUrl", caption: "The playable itself, for a preview tool or an <iframe>." },
];

/**
 * The stages the server reports, in the order it reports them.
 *
 * Mirrors the `stage` events in controller/snapshotEditor/previewSnapshot.js. A
 * stage that is not in this list simply does not render, so the two can be
 * changed independently.
 */
const STEPS = [
  { key: "build", label: "Building in the agent container" },
  { key: "upload", label: "Uploading the playable" },
];

const useStyles = makeStyles({
  // ── Header: what is being built, and for which network ──────────────────
  head: { display: "flex", alignItems: "center", gap: "12px" },
  fileName: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "13px",
    wordBreak: "break-all",
    lineHeight: 1.45,
  },
  network: {
    flex: "0 0 auto",
    fontSize: "12.5px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    borderRadius: "4px",
    padding: "2px 4px 2px 10px",
  },
  networkOption: { display: "flex", alignItems: "center", gap: "8px" },
  networkLogo: { width: "18px", height: "18px", objectFit: "contain" },

  // ── What to build: the language it speaks, and what it shows while it loads ─
  options: { display: "flex", gap: "8px", marginTop: "12px" },
  option: { flex: 1, minWidth: 0 },
  optionLabel: { fontSize: "11px", color: FIELD_LABEL, opacity: 0.85, marginBottom: "4px" },
  optionSelect: {
    width: "100%",
    fontSize: "12.5px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    borderRadius: "4px",
    padding: "2px 4px 2px 10px",
  },

  hint: { fontSize: "12px", color: FIELD_LABEL, opacity: 0.85, lineHeight: 1.5, marginTop: "10px" },

  // ── Progress: the stages, ticked off as they land ───────────────────────
  steps: { marginTop: "16px" },
  step: { display: "flex", alignItems: "center", gap: "9px", padding: "3px 0", fontSize: "13px" },
  stepIcon: {
    flex: "0 0 16px",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepDone: { color: "#a5d6a7" },
  // Engine fetch, dependency install, place in the build queue — BuildWaitLine.
  waitLine: { display: "block", fontSize: "12px", color: FIELD_LABEL, margin: "0 0 3px 25px" },
  stepPending: { color: FIELD_LABEL, opacity: 0.45 },
  // The dot a stage shows before it has started — a step with no mark at all
  // reads as a missing line rather than as one still to come.
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    border: `1px solid ${FIELD_LABEL}`,
    opacity: 0.5,
  },

  // ── Result ──────────────────────────────────────────────────────────────
  switcher: {
    display: "inline-flex",
    marginTop: "18px",
    padding: "2px",
    borderRadius: "5px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
  },
  segment: {
    minWidth: "78px",
    padding: "4px 14px",
    borderRadius: "3px",
    border: "none",
    background: "none",
    color: FIELD_LABEL,
    fontSize: "12.5px",
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "background-color 0.12s, color 0.12s",
    "&:hover": { color: "#fff" },
  },
  segmentOn: {
    backgroundColor: BAR_BG,
    color: "#fff",
    cursor: "default",
    "&:hover": { color: "#fff" },
  },

  urlRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "10px",
    padding: "8px 8px 8px 11px",
    border: BORDER,
    borderRadius: "6px",
    backgroundColor: CONTROL_BG,
  },
  // One line, clipped. The URL is long, carries a hash nobody reads, and is
  // going to the clipboard rather than being transcribed — wrapping it to three
  // lines would spend the dialog's height on characters that do no work.
  url: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "12px",
    color: "#dfe3ea",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textDecoration: "none",
    "&:hover": { color: ACCENT },
  },
  // Square icon buttons, matching the delivery dialog's link affordance. The
  // opener is an <a> so it keeps middle-click and "copy link address".
  iconButton: {
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    padding: 0,
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: `1px solid ${CONTROL_BORDER}`,
    color: FIELD_LABEL,
    cursor: "pointer",
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: ACCENT, borderColor: ACCENT },
  },
  iconButtonDone: {
    color: "#a5d6a7",
    borderColor: "rgba(165,214,167,0.5)",
    "&:hover": { color: "#a5d6a7", borderColor: "rgba(165,214,167,0.5)" },
  },
  caption: { fontSize: "11.5px", color: FIELD_LABEL, opacity: 0.85, marginTop: "8px", lineHeight: 1.5 },

  chips: { display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "14px" },
  chip: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "10px",
    border: BORDER,
    color: FIELD_LABEL,
    whiteSpace: "nowrap",
  },

  // ── Trouble ─────────────────────────────────────────────────────────────
  warning: { fontSize: "12px", color: "#e0b96a", marginTop: "12px", lineHeight: 1.5 },
  failure: { fontSize: "12.5px", color: "#ef9a9a", marginTop: "16px", lineHeight: 1.5 },
  producedLabel: {
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: FIELD_LABEL,
    marginTop: "12px",
  },
  produced: {
    fontFamily: "monospace",
    fontSize: "11.5px",
    color: FIELD_LABEL,
    marginTop: "5px",
    lineHeight: 1.7,
    wordBreak: "break-all",
  },
});

/** What a refusal from this controller means, in words. */
function describeFailure(response) {
  const error = response?.error;
  const code = typeof error === "string" ? error : error?.error;

  if (code === "PREVIEW_NEEDS_SINGLE_FILE") {
    const network = error?.network ? `The ${networkCaption(error.network)} build` : "This build";
    return `${network} came out as more than one file, so it cannot be served from a single ` +
      "link — only the html would be uploaded and the playable would open with its pieces " +
      "missing. Build it for Default instead, or export it as a zip.";
  }
  if (code === "BUILD_FAILED") {
    const first = (error?.failed || [])[0];
    return first?.error ? `The build failed: ${first.error}` : "The build failed.";
  }
  if (code === "PLAY_ENGINE_OUTPUT_IS_NOT_CONFIGURED") {
    return "The playEngine output mount is not configured on this server.";
  }
  if (code === "USER_HAS_NO_EXPORT_PERMISSION") {
    return "You do not have permission to export.";
  }
  // Re-running only overwrites this snapshot's own object, so a lost connection
  // is safe to retry — same bargain the export dialog makes.
  return describeJobFailure(response, { fallback: "Could not build the link.", repeatable: true });
}

export default function DialogShareLink({ editor, open, onClose, createSnackbar }) {
  const classes = useStyles();
  const [isBuilding, setIsBuilding] = useState(false);
  // The stages the server has reported so far, in arrival order.
  const [stagesSeen, setStagesSeen] = useState([]);
  const [progressEvents, setProgressEvents] = useState([]);
  const [connectionDown, setConnectionDown] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [producedFiles, setProducedFiles] = useState([]);
  const [network, setNetwork] = useState("default");
  const [language, setLanguage] = useState("en");
  const [loadingScreen, setLoadingScreen] = useState(DEFAULT_LOADING_SCREEN);
  const [linkView, setLinkView] = useState("demo");
  const [copied, setCopied] = useState(false);

  const fileName = editor.selectedFileName || "";

  /**
   * The languages this snapshot declares. The list summaries carry them (see the
   * server's listSnapshots), so the file does not have to be opened to ask.
   */
  const availableLanguages = useMemo(() => {
    const found = (editor.snapshots || []).find((item) => item.fileName === fileName);
    const languages = (found && found.languages) || [];
    return languages.length ? [...languages].sort() : ["en"];
  }, [editor.snapshots, fileName]);

  // A language the snapshot does not have would build an empty bundle, and the
  // selected file changes under this dialog whenever the editor's does.
  const effectiveLanguage = availableLanguages.includes(language) ? language : availableLanguages[0];
  const view = LINK_VIEWS.find((item) => item.id === linkView) || LINK_VIEWS[0];
  const url = result ? result[view.field] : null;

  function reset() {
    setResult(null);
    setError(null);
    setProducedFiles([]);
    setCopied(false);
  }

  async function run() {
    if (!fileName) return;

    setIsBuilding(true);
    reset();
    setStagesSeen([]);
    setProgressEvents([]);
    setConnectionDown(null);
    try {
      // The queued edits have to reach the file before it is built, or the link
      // would show the version on disk rather than the one on screen.
      await editor.flushSave();

      const response = await previewSnapshot(editor.baseID, fileName, {
        network,
        language: effectiveLanguage,
        loadingScreen,
        onProgress: (events) => {
          setProgressEvents(events);
          setStagesSeen(
            events.filter((event) => event.type === "stage").map((event) => event.stage)
          );
        },
        onConnectionState: (state) => setConnectionDown(state.down ? state : null),
      });

      if (!response.ok) {
        const detail = describeFailure(response);
        setError(detail);
        // A multi-file build names what it produced; showing the list is what
        // makes "more than one file" actionable rather than just a refusal.
        setProducedFiles(_.get(response, "error.files", []) || []);
        createSnackbar?.(`Share link failed: ${detail}`, "error", false);
        return;
      }

      setResult(response.data);
      setLinkView("demo");
      createSnackbar?.("Share link ready.", "success", false);
    } finally {
      setIsBuilding(false);
      setStagesSeen([]);
      setConnectionDown(null);
    }
  }

  async function copyLink() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (_err) {
      // No clipboard permission (or an insecure origin) — the URL is on screen
      // and selectable, so this is a convenience that failed, not an error.
      createSnackbar?.("Could not copy — select the link and copy it by hand.", "warning", false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isBuilding ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { backgroundColor: PANEL_BG } }}
    >
      <DialogTitle>Share link</DialogTitle>
      <DialogContent>
        <div className={classes.head}>
          <Typography className={classes.fileName} title={fileName}>
            {fileName || "No snapshot selected"}
          </Typography>
          <Select
            value={network}
            disabled={isBuilding}
            disableUnderline
            className={classes.network}
            onChange={(event) => {
              setNetwork(event.target.value);
              // Each network has its own link, so what is on screen belongs to
              // the one that produced it.
              reset();
            }}
            renderValue={(value) => networkCaption(value)}
          >
            {NETWORK_OPTIONS.map((option) => (
              <MenuItem key={option.name} value={option.name}>
                <span className={classes.networkOption}>
                  {option.logo && <img src={option.logo} alt="" className={classes.networkLogo} />}
                  {option.caption}
                </span>
              </MenuItem>
            ))}
          </Select>
        </div>

        {/* Each of these builds its own playable, so each gets its own link —
            changing one drops the link on screen rather than quietly replacing
            what it points at. */}
        <div className={classes.options}>
          <div className={classes.option}>
            <div className={classes.optionLabel}>Language</div>
            <Select
              value={effectiveLanguage}
              disabled={isBuilding || availableLanguages.length < 2}
              disableUnderline
              className={classes.optionSelect}
              onChange={(event) => {
                setLanguage(event.target.value);
                reset();
              }}
            >
              {availableLanguages.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={classes.option}>
            <div className={classes.optionLabel}>Loading screen</div>
            <Select
              value={loadingScreen}
              disabled={isBuilding}
              disableUnderline
              className={classes.optionSelect}
              onChange={(event) => {
                setLoadingScreen(event.target.value);
                reset();
              }}
            >
              {loadingScreenViews.map((screen) => (
                <MenuItem key={screen.id} value={screen.id}>
                  {screen.caption}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Said once, before anything has been built. Once there is a link on
            screen the link speaks for itself and this would just push it down. */}
        {!result && !isBuilding && !error && (
          <Typography className={classes.hint}>
            Builds this snapshot as a single playable on demo.playin.com.tr. Building again
            replaces what is behind the same link, so a link you have already sent keeps
            showing the current version.
          </Typography>
        )}

        {isBuilding && (
          <div className={classes.steps}>
            {STEPS.map((step) => {
              const state = stageState(stagesSeen, step.key);
              return (
                <React.Fragment key={step.key}>
                  <div className={`${classes.step} ${state === "pending" ? classes.stepPending : ""}`}>
                    <span className={classes.stepIcon}>
                      {state === "done" && <CheckIcon style={{ fontSize: 16 }} className={classes.stepDone} />}
                      {state === "active" && <CircularProgress size={13} thickness={5} />}
                      {state === "pending" && <span className={classes.dot} />}
                    </span>
                    {step.label}
                  </div>
                  {step.key === "build" && (
                    <BuildWaitLine
                      events={progressEvents}
                      active={isBuilding && state === "active"}
                      className={classes.waitLine}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* The step line stops moving when the connection goes; on its own that
            reads as a hang, and the build is not even involved. */}
        {isBuilding && connectionDown && (
          <Typography className={classes.warning}>
            Lost contact with the server {Math.round((connectionDown.sinceMs || 0) / 1000)}s ago —
            still retrying. The build keeps running there; leave this open.
          </Typography>
        )}

        {error && <Typography className={classes.failure}>{error}</Typography>}

        {producedFiles.length > 0 && (
          <>
            <div className={classes.producedLabel}>The build produced</div>
            <div className={classes.produced}>{producedFiles.join("  ·  ")}</div>
          </>
        )}

        {url && (
          <>
            <div className={classes.switcher}>
              {LINK_VIEWS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${classes.segment} ${item.id === linkView ? classes.segmentOn : ""}`}
                  onClick={() => {
                    setLinkView(item.id);
                    // "Copied" belongs to a URL, not to the dialog — switching
                    // away from the one that was copied has to drop it.
                    setCopied(false);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className={classes.urlRow}>
              <a className={classes.url} href={url} target="_blank" rel="noreferrer" title={url}>
                {url}
              </a>
              <Tooltip title="Open in a new tab">
                <a
                  className={classes.iconButton}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <OpenInNewIcon style={{ fontSize: 14 }} />
                </a>
              </Tooltip>
              <Tooltip title={copied ? "Copied" : "Copy the link"}>
                <button
                  type="button"
                  className={`${classes.iconButton} ${copied ? classes.iconButtonDone : ""}`}
                  onClick={copyLink}
                >
                  {copied ? <CheckIcon style={{ fontSize: 15 }} /> : <FileCopyIcon style={{ fontSize: 13 }} />}
                </button>
              </Tooltip>
            </div>

            <Typography className={classes.caption}>{view.caption}</Typography>

            <div className={classes.chips}>
              <span className={classes.chip}>{networkCaption(result.network)}</span>
              <span className={classes.chip}>{result.language}</span>
              {result.loadingScreen && (
                <span className={classes.chip}>{loadingScreenCaption(result.loadingScreen)}</span>
              )}
              <span className={classes.chip}>{formatBytes(result.sizeBytes)}</span>
              {result.engine?.engineVersion && (
                <span className={classes.chip}>engine {result.engine.engineVersion}</span>
              )}
              {result.engine?.commit && (
                <span className={classes.chip}>{result.engine.commit}</span>
              )}
            </div>
          </>
        )}

        {result && (result.warnings || []).length > 0 && (
          <div className={classes.warning}>
            {result.warnings.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBuilding}>
          Close
        </Button>
        <Button color="primary" variant="contained" disabled={isBuilding || !fileName} onClick={run}>
          {result ? "Rebuild" : "Build link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
